import { Col } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { TextArea } from '@amzn/stencil-react-components/text-area';

import { useMessages } from '../../../shared/i18n/useMessages';

const PROMPT_BUNDLE = 'features.audit-whiz.translations.generateQuestions';

type PromptKey =
  | 'promptSectionTitle'
  | 'promptPlaceholder'
  | 'promptMinimumHint'
  | 'promptCharsRemaining'
  | 'promptCharsEntered';

/** Minimum prompt length before generation is allowed. Exported so the
 *  page and its tests share one source of truth. */
export const PROMPT_MIN_LENGTH = 100;

type Translate = (key: PromptKey) => string;
type Format = (key: PromptKey, args: Record<string, number>) => string;

/**
 * Resolves the live counter copy for the three states:
 * - empty prompt → neutral "Minimum 100 characters" hint;
 * - 1..99 chars → "N more characters needed";
 * - >=100 chars → "N characters" running total.
 */
function resolveCounterText(
  count: number,
  remaining: number,
  f: Format,
  t: Translate,
): string {
  if (count === 0) return t('promptMinimumHint');
  if (count < PROMPT_MIN_LENGTH) return f('promptCharsRemaining', { remaining });
  return f('promptCharsEntered', { count });
}

export interface PromptContextSectionProps {
  /** Current prompt text (controlled by the page). */
  readonly value: string;
  /** Called with the new prompt text on every edit. */
  readonly onChange: (value: string) => void;
  /** data-testid for the underlying textarea. */
  readonly dataTestId?: string;
}

/**
 * The prompt-input fields: a single textarea where the auditor describes what
 * they want Astra to generate, plus a live character counter and a
 * minimum-length hint. Card-less — it renders inside the merged "Define your
 * audit questions" card owned by the page (which supplies the card title,
 * description, and Source badge).
 *
 * Presentation + counter only — it does not own the disabled/enabled decision
 * for the Generate button. The page derives that from
 * `value.trim().length >= PROMPT_MIN_LENGTH` so the whole page reacts to one
 * source of truth.
 */
export function PromptContextSection({
  value,
  onChange,
  dataTestId,
}: PromptContextSectionProps): JSX.Element {
  const { t, f } = useMessages<PromptKey>(PROMPT_BUNDLE);

  // Measure the trimmed length so the counter agrees with the page's
  // Generate gate (`prompt.trim().length >= PROMPT_MIN_LENGTH`) — otherwise
  // whitespace-padded input could show a satisfied count while Generate
  // stays disabled.
  const count = value.trim().length;
  const remaining = PROMPT_MIN_LENGTH - count;

  // Three states: an empty prompt shows the neutral minimum hint; a
  // partially-filled prompt nudges with how many more characters are
  // needed; at/above the minimum, show the running total.
  const counterText = resolveCounterText(count, remaining, f, t);

  return (
    <Col gridGap="dimensions.spacing.100" width="100%">
      <TextArea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('promptPlaceholder')}
        rows={6}
        aria-label={t('promptSectionTitle')}
        data-testid={dataTestId}
      />

      <Text
        fontSize="T100"
        color="color.text.secondary"
        data-testid={dataTestId ? `${dataTestId}-counter` : undefined}
      >
        {counterText}
      </Text>
    </Col>
  );
}
